import { useContext, useMemo, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { getGuarantorStatus, isValidEmail, toast } from "../../helper/common";
import ApplicationContext from "../../context/application/ApplicationContext";
import AuthContext from "../../context/auth/AuthContext";
import SkorProtectSelector from "../SkorProtectSelector/SkorProtectSelector";
import { Avatar, Badge, Button, Icon, Modal, TextField } from "../design";
import GuarantorSelect from "../GuarantorSelect/GuarantorSelect";
import { GuarantorStatusBadge } from "./GuarantorCardParts";
import "./NewApplicationModal.css";

const NewApplicationModal = ({ isOpen, onClose, onSuccess }) => {
  const { _t } = useTranslator();
  const { createApplication, myApplications } = useContext(ApplicationContext);
  const { user } = useContext(AuthContext);

  const currentLang = localStorage.getItem("i18nextLng").toLowerCase() || "en";

  const defaultTitle =
    currentLang === "es"
      ? `Aplicación de ${user?.fname}`
      : `${user?.fname}'s Application`;

  const selfName =
    [user?.fname, user?.lname].filter(Boolean).join(" ").trim() || user?.email;

  const [appTitle, setAppTitle] = useState(defaultTitle);
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [participants, setParticipants] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [creating, setCreating] = useState(false);
  const [skorProtect, setSkorProtect] = useState(true);
  const [selectedGuarantorIds, setSelectedGuarantorIds] = useState([]);

  // The tenant's guarantors that can join (a declined one cannot)
  const guarantors = useMemo(
    () =>
      (Array.isArray(user?.gurantor_added_id) ? user.gurantor_added_id : [])
        .filter(
          (entry) =>
            entry?._id && entry?.gurantor_email && entry?.status !== "rejected"
        )
        .map((entry) => ({
          id: entry._id,
          name: entry.gurantor_name?.trim() || entry.gurantor_email,
          email: entry.gurantor_email.toLowerCase(),
          statusData: getGuarantorStatus(entry.status, entry.guarantor_id),
        })),
    [user?.gurantor_added_id]
  );

  const handleAddEmail = () => {
    const trimmedName = nameInput.trim();
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (!trimmedName) {
      setEmailError(_t("nameRequired", "Full name is required"));
      return;
    }
    if (!trimmedEmail) return;
    if (!isValidEmail(trimmedEmail)) {
      setEmailError(_t("invalidEmail"));
      return;
    }
    if (trimmedEmail === user?.email?.toLowerCase()) {
      setEmailError(_t("cannotAddYourself"));
      return;
    }
    if (participants.some((p) => p.email === trimmedEmail)) {
      setEmailError(_t("emailAlreadyAdded"));
      return;
    }
    if (participants.length >= 4) {
      setEmailError(
        _t("maxParticipantsReached", "Maximum of 4 participants allowed")
      );
      return;
    }
    setParticipants((prev) => [
      ...prev,
      { name: trimmedName, email: trimmedEmail },
    ]);
    setNameInput("");
    setEmailInput("");
    setEmailError("");
  };

  const selectedGuarantors = guarantors.filter((guarantor) =>
    selectedGuarantorIds.includes(guarantor.id)
  );

  const handleRemoveEmail = (email) => {
    setParticipants((prev) => prev.filter((p) => p.email !== email));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleCreate = async () => {
    // An application with only the current user is allowed; the duplicate
    // check below stops a second one
    const newEmails = [
      ...participants.map((p) => p.email.toLowerCase()),
      ...selectedGuarantors.map((g) => g.email),
    ].sort();
    const currentUserEmail = user?.email?.toLowerCase();
    const hasDuplicate = (myApplications ?? []).some((app) => {
      const existing = (app.participants ?? [])
        .map((p) => p.email?.toLowerCase())
        .filter((e) => e && e !== currentUserEmail)
        .sort();
      return (
        existing.length === newEmails.length &&
        existing.every((e, i) => e === newEmails[i])
      );
    });
    if (hasDuplicate) {
      toast.error(_t("alreadyExistingApplication"));
      return;
    }
    try {
      setCreating(true);
      await createApplication({
        participants,
        title: appTitle.trim() || defaultTitle,
        skor_protect: skorProtect,
        guarantor_ids: selectedGuarantorIds,
      });
      onSuccess();
      handleClose();
      toast.success(_t("applicationCreatedSuccess"));
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? _t("failedToCreateApplication")
      );
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setAppTitle(defaultTitle);
    setNameInput("");
    setEmailInput("");
    setParticipants([]);
    setEmailError("");
    setSkorProtect(true);
    setSelectedGuarantorIds([]);
    onClose();
  };

  const handleTitleBlur = () => {
    if (!appTitle.trim()) setAppTitle(defaultTitle);
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={_t("newApplication")}
      closeLabel={_t("close")}
      align="start"
      actionsLayout="end"
      actions={
        <>
          <Button variant="secondary" size="lg" onClick={handleClose}>
            {_t("cancel")}
          </Button>
          <Button size="lg" loading={creating} onClick={handleCreate}>
            {_t("createApplication")}
          </Button>
        </>
      }
    >
      <div className="new-application-form">
        <TextField
          id="new-application-title"
          label={_t("applicationName", "Application name")}
          value={appTitle}
          onChange={(e) => setAppTitle(e.target.value)}
          onBlur={handleTitleBlur}
        />

        <div className="new-application-invite">
          <div>
            <h3 className="new-application-invite__title">
              {_t("inviteFlatmatesByEmail")}
            </h3>
            <p className="new-application-invite__text">
              {_t(
                "typeEmailAndPressEnter",
                "The invited flatmates will receive a request to confirm they accept being part of this application."
              )}
            </p>
          </div>

          <div className="new-application-invite__row">
            <TextField
              id="new-application-name"
              placeholder={_t("fullName", "Full Name")}
              value={nameInput}
              error={emailError ? true : undefined}
              onChange={(e) => {
                setNameInput(e.target.value);
                setEmailError("");
              }}
              onKeyDown={handleKeyDown}
            />
            <TextField
              id="new-application-email"
              type="email"
              placeholder="name@email.com"
              value={emailInput}
              error={emailError || undefined}
              onChange={(e) => {
                setEmailInput(e.target.value.toLowerCase());
                setEmailError("");
              }}
              onKeyDown={handleKeyDown}
            />
            <Button
              variant="secondary"
              size="lg"
              className="new-application-invite__add"
              onClick={handleAddEmail}
              disabled={!emailInput.trim() || !nameInput.trim()}
            >
              {_t("add")}
            </Button>
          </div>
        </div>

        <div className="new-application-people">
          <div className="new-application-person">
            <Avatar name={selfName} size="md" />
            <div className="new-application-person__info">
              <span className="new-application-person__name" title={selfName}>
                {selfName}
              </span>
              <span
                className="new-application-person__email"
                title={user?.email}
              >
                {user?.email}
              </span>
            </div>
            <Badge tone="neutral">{_t("you", "You")}</Badge>
          </div>
          {participants.map(({ name, email }) => (
            <div key={email} className="new-application-person">
              <Avatar name={name} size="md" />
              <div className="new-application-person__info">
                <span className="new-application-person__name">{name}</span>
                <span className="new-application-person__email">{email}</span>
              </div>
              <Badge tone="neutral">
                {_t("invitationWillBeSent", "Invitation will be sent")}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={_t("remove")}
                iconStart={<Icon name="close" />}
                onClick={() => handleRemoveEmail(email)}
              />
            </div>
          ))}
          {selectedGuarantors.map((guarantor) => (
            <div key={guarantor.id} className="new-application-person">
              <Avatar name={guarantor.name} size="md" />
              <div className="new-application-person__info">
                <span className="new-application-person__nameRow">
                  <span
                    className="new-application-person__name"
                    title={guarantor.name}
                  >
                    {guarantor.name}
                  </span>
                  <Badge tone="neutral" size="xs">
                    {_t("guarantor", "Guarantor")}
                  </Badge>
                </span>
                <span
                  className="new-application-person__email"
                  title={guarantor.email}
                >
                  {guarantor.email}
                </span>
              </div>
              <GuarantorStatusBadge statusData={guarantor.statusData} />
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={_t("remove")}
                iconStart={<Icon name="close" />}
                onClick={() =>
                  setSelectedGuarantorIds((prev) =>
                    prev.filter((id) => id !== guarantor.id)
                  )
                }
              />
            </div>
          ))}
        </div>

        {guarantors.length > 0 && (
          <GuarantorSelect
            id="new-application-guarantors"
            label={_t("addGuarantors", "Add guarantors")}
            placeholder={_t("selectGuarantors", "Select guarantors")}
            guarantors={guarantors}
            value={selectedGuarantorIds}
            onChange={setSelectedGuarantorIds}
          />
        )}

        <SkorProtectSelector value={skorProtect} onChange={setSkorProtect} />
      </div>
    </Modal>
  );
};

export default NewApplicationModal;
